import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { toast } from 'sonner';
import { storage, authAPI, SESSION_EXPIRED_EVENT } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = storage.getJSON('user');
    if (savedUser) {
      setUser(savedUser);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      toast.info('Your session has expired. Please sign in again.', { id: 'session-expired' });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // True only after a deliberate sign-out, so the login page doesn't offer to "return" there.
  const [signedOut, setSignedOut] = useState(false);

  const login = (userData) => {
    // JWT is set as an httpOnly cookie by the backend; we only persist the
    // non-sensitive user object for UI bootstrapping.
    storage.set('user', userData);
    setSignedOut(false);
    setUser(userData);
  };

  const logout = () => {
    authAPI.logout().catch(() => {});
    storage.remove('user');
    setSignedOut(true);
    setUser(null);
  };

  const authValue = useMemo(() => ({ user, signedOut, login, logout }), [user, signedOut]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F0F4F8' }}>
        <div style={{ color: '#005DEB', fontSize: '1.25rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}
